// Lib
import Document from '@tiptap/extension-document';
import StarterKit from '@tiptap/starter-kit';
import TaskList from '@tiptap/extension-task-list';
import UniqueID from '@tiptap-pro/extension-unique-id';
import Underline from '@tiptap/extension-underline';

// Custom extensions
import { CodeMark } from '../CodeMark';
import { Heading } from '../Heading';
import { InlineComment } from '../inlineComment/InlineComment';
import { Link } from '../hyperlink/Link';
import { OldMention } from '../mention/OldMention';
import { TextColor } from '../TextColor';
import { TextHighlight } from '../TextHighlight';
import { TextStyle } from '../TextStyle';
import { TaskListDocument } from './TaskListDocument';
import { TaskListItemSchema } from './TaskListItemSchema';
import { TaskListTitle } from './TaskListTitle';
import { InlineBoardLink } from '../inlineBoardLink/InlineBoardLink';

/**
 * The single-editor task list's schema, for parsing task list content outside of the editor.
 *
 * Mirrors the extensions `useTaskListEditorExtensions` composes, minus everything that only
 * affects editing, with two deliberate differences:
 *   - the document accepts any block or a title in any position, rather than `TaskListDocument`'s
 *     `taskListTitle? taskList`, so a leading title, heading or paragraph survives parsing and can
 *     be inspected rather than being silently dropped;
 *   - `Heading` is included even though the editor has no heading node, so a heading copied from
 *     elsewhere survives parsing and can be transformed into the list's title. `TaskListTitle`'s
 *     parse rule outranks the generic `h2` rule by priority, so a copied title stays a title.
 *
 * Nothing outside the editor's own schema may be added here: content parsed with a type the editor
 * lacks fails to load into it, and Tiptap swallows the error and substitutes empty content. That is
 * why `TextMention` is absent - the editor composes no `suggestionsManager`, so it has no
 * `textMention` mark, and an `@mention` copied out of a card parses to plain text instead.
 *
 * Composes `TaskListItemSchema` rather than `TaskListItem`, so nothing here reaches the node view's
 * SCSS and Roma imports and the list can be built on the server.
 */
export const SINGLE_EDITOR_TASK_LIST_STATIC_TIPTAP_EXTENSIONS = [
    StarterKit.configure({
        document: false,
        heading: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        horizontalRule: false,
        listItem: false,
    }),
    Document.extend({ content: '(title | block)+' }),
    TaskListTitle,
    Heading.configure({ levels: [1, 2, 3, 4, 5, 6] }),
    TaskList,
    // Supplies the `taskId` attr, so a copy made in the editor can be recognised by it.
    UniqueID.configure({ attributeName: 'taskId', types: ['taskItem'] }),
    // Nested, or sub-tasks flatten into sibling top level task lists when parsing.
    TaskListItemSchema.configure({ nested: true }),
    Underline,
    TextStyle,
    TextColor,
    TextHighlight,
    OldMention,
    Link,
    CodeMark,
    InlineComment,
    InlineBoardLink,
];

/**
 * The document a stored single-editor task list has to be, as the editor itself defines it.
 *
 * - The list above deliberately loosens the document rule so pasted content survives parsing.
 * - This one keeps `TaskListDocument`'s `taskListTitle? taskList`, which is what a stored
 *   document is held to, and drops `Heading` for the same reason.
 */
export const SINGLE_EDITOR_TASK_LIST_DOCUMENT_TIPTAP_EXTENSIONS = [
    ...SINGLE_EDITOR_TASK_LIST_STATIC_TIPTAP_EXTENSIONS.filter(
        (extension) => extension.name !== 'doc' && extension.name !== 'heading',
    ),
    TaskListDocument,
];
